
import {
  computed, defineComponent, onBeforeUnmount, PropType, ref, watch,
} from 'vue'
import axios from 'axios'
import { Webinar, WebinarSchedule } from '@/types/webinar.d'
import YouTube from 'vue3-youtube'
import AdminButtons from '@/vue/public/AdminButtons.vue'
import getCountdown from '@/scripts/countdown'
import IconArrowAlt from '@/vue/icons/IconArrowAlt.vue'
import IconEye from '@/vue/icons/IconEye.vue'
import { useStore } from 'vuex'
import { key } from '@/vue/store'
import Loading from '@/vue/public/Loading.vue'
import IconReload from '@/vue/icons/IconReload.vue'
import { useI18n } from 'vue-i18n'
import Timeout from '@/types/timeout.d'
import IconPlay from '@/vue/icons/IconPlay.vue'
import VideoSlider from '@/vue/public/VideoSlider.vue'
import Button from '@/vue/public/Button.vue'
import Buttons from './Buttons.vue'
import Chat from './Chat.vue'

const playerVars = {
  controls: 0,
  disablekb: 1,
  fs: 0,
  iv_load_policy: 3,
  modestbranding: 1,
  rel: 0,
  showinfo: 0,
  origin: window.location.host,
  playsinline: 1,
  autoplay: 0,
}

declare global {
  interface Window {
    BASE_URI: string
    footerText: string
    webinarsLogo: string
  }
}

export default defineComponent({
  name: 'Webinar',
  props: {
    webinar: {
      type: Object as PropType<Webinar>,
      required: true,
    },
    schedule: {
      type: Object as PropType<WebinarSchedule>,
      required: true,
    },
    archived: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Button,
    VideoSlider,
    IconPlay,
    IconReload,
    Loading,
    Buttons,
    Chat,
    YouTube,
    AdminButtons,
    IconArrowAlt,
    IconEye,
  },
  setup(props) {
    const { t } = useI18n()

    const youtubeIframe = ref<typeof YouTube.methods>()
    const dueDate = ref(Date.now())
    const now = ref(Date.now())
    const videoDuration = ref(0)
    const countdown = computed(() => getCountdown(Math.round((dueDate.value - now.value) / 1000), t('public.daysLetter')))
    const loadingVideo = ref(true)

    let youtubeReady = false
    const youtubePlay = ref(false)

    const updateDueDate = () => {
      dueDate.value = Date.now() - (props.schedule.left * 1000)
    }
    updateDueDate()
    watch(() => props.schedule, updateDueDate)

    const timeInterval = setInterval(() => {
      now.value = Date.now()
    }, 100)
    onBeforeUnmount(() => clearInterval(timeInterval))

    let playerStateInterval: Timeout
    const initYoutube = () => {
      youtubeReady = true
      if (!props.schedule.finished && youtubeIframe.value && !props.archived) {
        playerStateInterval = setInterval(() => {
          if (youtubeIframe.value && youtubeIframe.value.getPlayerState() !== 1) {
            if (!props.schedule.finished && youtubePlay.value) {
              youtubeIframe.value.playVideo()
            }
          } else {
            clearInterval(playerStateInterval)
          }
        }, 100)
      }
      videoDuration.value = youtubeIframe.value?.getDuration() || 0
    }
    onBeforeUnmount(() => clearInterval(playerStateInterval))

    const store = useStore(key)
    const administration = computed(() => store.state.userAdmin && !props.schedule.endAt)

    let loadingTimeout: Timeout
    const stateChange = ({ data }: { data: number }) => {
      clearTimeout(loadingTimeout)
      if (youtubeIframe.value) {
        if (data === 1) {
          loadingTimeout = setTimeout(() => {
            loadingVideo.value = false
          }, 4000)
          youtubePlay.value = true
        } else {
          loadingVideo.value = true
          if (data === 2) {
            if (props.archived) {
              youtubePlay.value = false
            } else {
              youtubeIframe.value.seekTo(props.schedule.left > 0 ? props.schedule.left : 0, true)
              youtubeIframe.value.playVideo()
            }
          }
        }
      }
    }

    const videoTiming = ref(0)
    if (props.archived) {
      const vtt = setInterval(() => {
        if (youtubeIframe.value && youtubePlay.value) {
          videoTiming.value = Math.floor(youtubeIframe.value.getCurrentTime())
        }
      }, 1000)
      onBeforeUnmount(() => {
        clearInterval(vtt)
      })
    }

    const users = ref([])
    let usersTimeout: Timeout
    const getUsers = async () => {
      clearTimeout(usersTimeout)
      users.value = await axios.get(`/schedule/${props.schedule.id}/users`, props.archived ? {
        params: {
          webinar: props.webinar.id,
          timing: videoTiming.value,
        },
      } : {}).then(({ data }) => data).catch(() => [])
      usersTimeout = setTimeout(getUsers, 3000)
    }
    getUsers()

    const loadingFinishing = ref(false)
    const finishTranslation = async () => {
      // eslint-disable-next-line no-alert
      if (store.state.userAdmin && !props.schedule.endAt && !loadingFinishing.value && confirm(t('public.confirmFinish'))) {
        loadingFinishing.value = true
        await axios.post(`/webinars/${props.schedule.id}/finish`)
      }
    }

    const showVideo = computed(() => !props.schedule.finished
      && !(videoDuration.value && props.schedule.left > videoDuration.value))

    const reload = () => document.location.reload()

    const utm = computed(() => store.state.userUtm)
    const addUtm = (href: string) => {
      let l: string = href
      if (utm.value) {
        l += l.includes('?') ? '&' : '?'
        l += utm.value.replace(/^\?/, '')
      }
      return l
    }

    const onContextMenu = (e: MouseEvent) => {
      if (!store.state.userAdmin) {
        e.preventDefault()
      }
    }

    const startOnClick = () => {
      if (youtubeReady && youtubeIframe.value) {
        if (youtubePlay.value) {
          if (props.archived) {
            youtubeIframe.value.pauseVideo()
            youtubePlay.value = false
          }
        } else {
          if (!props.archived) {
            youtubeIframe.value.seekTo(props.schedule.left > 0 ? props.schedule.left : 0, true)
          }
          youtubeIframe.value.playVideo()
          youtubePlay.value = true
        }
      }
    }

    const setTiming = (time: number) => {
      if (props.archived) {
        youtubeIframe.value?.seekTo(time, true)
        videoTiming.value = time
      }
    }

    const archivedLink = `${document.location.pathname}?archived=1`

    return {
      mainPage: window.BASE_URI,
      logo: window.webinarsLogo,
      footer: window.footerText,
      countdown,
      initYoutube,
      youtubeIframe,
      playerVars,
      stateChange,
      users,
      administration,
      finishTranslation,
      loadingFinishing,
      showVideo,
      loadingVideo,
      reload,
      t,
      addUtm,
      onContextMenu,
      startOnClick,
      youtubePlay,
      videoTiming,
      videoDuration,
      setTiming,
      baseURI: window.BASE_URI,
      archivedLink,
    }
  },
})
